import React, { ComponentProps, useCallback, useId, useMemo, useRef, useState } from "react";
import {
    TextInput,
    TextInputProps,
    StyleSheet,
    PixelRatio,
    StyleProp,
    TextStyle,
    Platform,
} from "react-native";
import { useTheme } from "../hooks/use-theme";
import { useMaskedInputProps } from "react-native-mask-input";
import { Mask } from "react-native-mask-input/src/formatWithMask.types";
import BaseField, { BaseFieldProps } from "./BaseField";
import mergeRefs from "merge-refs";
import DoneAccessoryBar from "./DoneAccessoryBar";

const FONT_SCALE = PixelRatio.getFontScale();

const BASE_PADDING_VERTICAL = 14;
const MULTILINE_PADDING_VERTICAL =
    BASE_PADDING_VERTICAL - (FONT_SCALE - 1) * (BASE_PADDING_VERTICAL / 2);

export type TextFieldProps = Omit<BaseFieldProps, "component" | "hasValue"> &
    Omit<TextInputProps, "theme" | "placeholder"> & {
        mask?: Mask;
        showObfuscatedValue?: boolean;
        placeholderFillCharacter?: string;
        obfuscationCharacter?: string;
        maskAutoComplete?: boolean;
        isRnTextInput?: boolean;
        inputStyle?: StyleProp<TextStyle>;
        withIosDoneButton?: boolean;
        textInputComponent?: React.ComponentType<TextInputProps>;
    };

const TextField = ({
    style,
    inputStyle,
    mask,
    selection,
    showObfuscatedValue,
    placeholderFillCharacter,
    obfuscationCharacter,
    maskAutoComplete = true,
    onChangeText,
    label,
    disabled,
    value,
    left,
    right,
    leftColor,
    rightColor,
    labelLeftPadding,
    error,
    showErrorText,
    onFocus: propsOnFocus,
    onBlur: propsOnBlur,
    placeholder,
    isSelfContained,
    multiline,
    withIosDoneButton = multiline,
    enterKeyHint,
    submitBehavior,
    textInputComponent: TextInputComponent = TextInput,
    ref,
    ...props
}: TextFieldProps & { ref?: React.Ref<TextInput> }) => {
    const uniqueId = useId();
    const [isFocused, setIsFocused] = useState(false);
    const styles = useStyles();
    const maskedInputProps = useMaskedInputProps({
        value,
        mask,
        maskAutoComplete,
        obfuscationCharacter,
        onChangeText,
        placeholderFillCharacter,
        showObfuscatedValue,
    });

    const onFieldChangeText = useCallback<
        NonNullable<ComponentProps<typeof TextInput>["onChangeText"]>
    >(
        (e) => {
            maskedInputProps.onChangeText(e.trimStart());
        },
        [maskedInputProps],
    );

    const onFocus = useCallback(() => {
        setIsFocused(true);
        propsOnFocus?.();
    }, [propsOnFocus]);

    const onBlur = useCallback(() => {
        setIsFocused(false);
        propsOnBlur?.();
    }, [propsOnBlur]);

    const baseRef = useRef<TextInput>(null);
    // `mergeRefs` composes refs without reading `.current`; the rule can't tell.
    // eslint-disable-next-line react-hooks/refs
    const refs = mergeRefs(baseRef, ref);
    const component = useMemo(
        () => (
            <TextInputComponent
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore  workaround to have the correct selection color on android
                selectionColor={null}
                selection={maskedInputProps.selection || selection}
                style={[
                    styles.textInput,
                    error && styles.errorTextInput,
                    disabled && styles.disabledTextInput,
                    multiline && styles.textInputMultiline,
                    inputStyle,
                    props.textAlign && { textAlign: props.textAlign }, // Necessary to ensure native/web consistency
                    !multiline && { lineHeight: undefined },
                ]}
                onChangeText={onFieldChangeText}
                editable={!disabled}
                value={value}
                {...props}
                enterKeyHint={multiline ? (Platform.OS === "ios" ? "enter" : "done") : enterKeyHint}
                submitBehavior={multiline ? "newline" : submitBehavior ? submitBehavior : undefined}
                multiline={multiline}
                inputAccessoryViewID={withIosDoneButton ? uniqueId : undefined}
            />
        ),
        [
            TextInputComponent,
            disabled,
            error,
            inputStyle,
            maskedInputProps.selection,
            multiline,
            onFieldChangeText,
            props,
            selection,
            styles.disabledTextInput,
            styles.errorTextInput,
            styles.textInput,
            styles.textInputMultiline,
            value,
            enterKeyHint,
            submitBehavior,
            uniqueId,
            withIosDoneButton,
        ],
    );

    return (
        <>
            <BaseField
                style={[multiline ? styles.multiline : {}, style]}
                value={!!value}
                label={label !== undefined ? label : ""}
                placeholder={placeholder !== undefined ? placeholder : undefined}
                disabled={disabled}
                left={left}
                right={right}
                labelLeftPadding={labelLeftPadding}
                leftColor={leftColor}
                rightColor={rightColor}
                error={error}
                showErrorText={showErrorText}
                onFocus={onFocus}
                onBlur={onBlur}
                isFocused={props.isFocused || isFocused}
                isSelfContained={isSelfContained}
                componentRef={refs}
                component={component}
            />
            {withIosDoneButton && Platform.OS === "ios" && <DoneAccessoryBar nativeId={uniqueId} />}
        </>
    );
};

const useStyles = () => {
    const { colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                inputAccessoryView: {
                    flexDirection: "row",
                    justifyContent: "flex-end",
                },
                textInput: {
                    ...fonts.regular,
                    padding: 0,
                },
                errorTextInput: {
                    color: colors.error,
                },
                multiline: {
                    height: fonts.regular.fontSize * 8 + MULTILINE_PADDING_VERTICAL,
                    paddingVertical: MULTILINE_PADDING_VERTICAL,
                },
                textInputMultiline: {
                    maxHeight: "100%",
                    minHeight: "100%",
                    textAlignVertical: "top",
                },
                disabledTextInput: {
                    color: colors.fieldLabel,
                },
            }),
        [colors.error, colors.fieldLabel, fonts.regular],
    );
};

export default TextField;
